import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { createFileRoute } from '@tanstack/react-router';
import SuperAdminLayout from '@/components/SuperAdminLayout';
import { apiFetch } from '@/utils/api';

function NewTenantPage() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        base_currency: 'HUF',
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const generateSlug = (name: string) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.value;
        setFormData(prev => ({
            ...prev,
            name,
            slug: generateSlug(name),
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const response = await apiFetch('/api/superadmin/organizations', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    slug: formData.slug,
                    description: formData.description || null,
                    base_currency: formData.base_currency,
                }),
            });

            if (!response.ok) {
                const data = await response.json();
                setError(data.detail || 'Failed to create tenant');
                return;
            }

            const data = await response.json();
            navigate({ to: `/superadmin/tenants/${data.id}` });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <SuperAdminLayout>
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate({ to: '/superadmin/tenants' })}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Create New Tenant</h1>
                        <p className="text-slate-600 mt-1">Set up a new organization</p>
                    </div>
                </div>

                <div className="bg-white rounded-lg border border-slate-200 p-6 max-w-2xl">
                    {error && (
                        <Alert variant="destructive" className="mb-6">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-slate-900 mb-2">
                                Organization Name *
                            </label>
                            <Input
                                id="name"
                                name="name"
                                type="text"
                                placeholder="e.g., Acme Restaurant Group"
                                value={formData.name}
                                onChange={handleNameChange}
                                disabled={isLoading}
                                required
                            />
                            <p className="text-xs text-slate-600 mt-1">The display name for this organization</p>
                        </div>

                        <div>
                            <label htmlFor="slug" className="block text-sm font-medium text-slate-900 mb-2">
                                Slug *
                            </label>
                            <Input
                                id="slug"
                                name="slug"
                                type="text"
                                placeholder="e.g., acme-restaurant"
                                value={formData.slug}
                                onChange={handleChange}
                                disabled={isLoading}
                                required
                                pattern="^[a-z0-9-]+$"
                            />
                            <p className="text-xs text-slate-600 mt-1">URL-safe identifier (lowercase, hyphens only)</p>
                        </div>

                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-slate-900 mb-2">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                placeholder="Optional description of the organization"
                                value={formData.description}
                                onChange={handleChange}
                                disabled={isLoading}
                                rows={3}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="base_currency" className="block text-sm font-medium text-slate-900 mb-2">
                                Base Currency *
                            </label>
                            <select
                                id="base_currency"
                                name="base_currency"
                                value={formData.base_currency}
                                onChange={handleChange}
                                disabled={isLoading}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="HUF">HUF - Hungarian Forint</option>
                                <option value="EUR">EUR - Euro</option>
                                <option value="USD">USD - US Dollar</option>
                                <option value="GBP">GBP - British Pound</option>
                                <option value="CHF">CHF - Swiss Franc</option>
                            </select>
                            <p className="text-xs text-slate-600 mt-1">Default currency for this organization</p>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Creating...' : 'Create Tenant'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate({ to: '/superadmin/tenants' })}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                        </div>
                </form>
                </div>
                </div>
            </SuperAdminLayout>
    );
}

export const Route = createFileRoute('/superadmin/tenants/new')({
    component: NewTenantPage,
});
